/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, Landmark, Stamp } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DialogFooter } from '@/components/ui/dialog';
import { useBrand } from '@/lib/whitelabel';
import { createInvoice } from '@/lib/invoicing';
import { emptyLine, euro, computeFiscalPreview } from './fiscalPreview';

export const InvoiceForm = ({ onClose, onSaved }) => {
  const brand = useBrand();
  const [customer, setCustomer] = useState({ full_name: '', tax_code: '', address: '', email: '' });
  const [lines, setLines] = useState([emptyLine()]);
  const [paymentMethod, setPaymentMethod] = useState('Bonifico');
  const [paymentTerms, setPaymentTerms] = useState('Pagamento immediato');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Modulo fiscale IT: esenzione Art. 10 (Natura N4) + bollo virtuale automatico
  const [vatExempt, setVatExempt] = useState(true);
  const [bolloToCustomer, setBolloToCustomer] = useState(true);

  const totals = useMemo(() => computeFiscalPreview(lines, { vatExempt, bolloToCustomer }), [lines, vatExempt, bolloToCustomer]);

  const addLine = () => setLines((l) => [...l, emptyLine()]);
  const rmLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines((l) => l.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const handleSave = async () => {
    if (!customer.full_name.trim()) {
      toast.error('Inserisci il nome del cliente');
      return;
    }
    if (!lines.length || lines.some((l) => !l.description || Number(l.unit_price_eur) < 0)) {
      toast.error('Ogni riga deve avere descrizione e prezzo valido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer: { ...customer, tax_code: customer.tax_code || null, address: customer.address || null, email: customer.email || null },
        lines: lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 1,
          unit_price_eur: Number(l.unit_price_eur) || 0,
          vat_pct: Number(l.vat_pct) || 0,
        })),
        payment_method: paymentMethod || null,
        payment_terms: paymentTerms || null,
        notes: notes || null,
        vat_exempt: vatExempt,
        exemption_natura: 'N4',
        apply_bollo: true,
        bollo_charged_to_customer: bolloToCustomer,
        issuer_name: brand.legalOwner || null,
        issuer_vat: brand.legalVat || null,
        issuer_address: brand.legalAddress || null,
      };
      const res = await createInvoice(payload);
      toast.success(`Fattura n° ${res.number} creata`);
      onSaved?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione fattura');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="invoice-form">
      <div>
        <h3 className="text-sm font-semibold text-primary mb-2">Cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Nome completo *</Label>
            <Input value={customer.full_name} onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })} data-testid="invoice-customer-name" />
          </div>
          <div>
            <Label>C.F. / P.IVA</Label>
            <Input value={customer.tax_code} onChange={(e) => setCustomer({ ...customer, tax_code: e.target.value })} data-testid="invoice-customer-taxcode" />
          </div>
          <div className="md:col-span-2">
            <Label>Indirizzo</Label>
            <Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="invoice-customer-address" />
          </div>
          <div className="md:col-span-2">
            <Label>Email</Label>
            <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="invoice-customer-email" />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-primary">Righe fattura</h3>
          <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi riga
          </Button>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={l._key || i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg border bg-muted/30">
              <div className="col-span-12 md:col-span-5">
                <Label className="text-xs">Descrizione</Label>
                <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} data-testid={`invoice-line-desc-${i}`} />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label className="text-xs">Q.tà</Label>
                <Input type="number" step="0.01" min="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} data-testid={`invoice-line-qty-${i}`} />
              </div>
              <div className="col-span-4 md:col-span-2">
                <Label className="text-xs">Prezzo un. €</Label>
                <Input type="number" step="0.01" min="0" value={l.unit_price_eur} onChange={(e) => updateLine(i, { unit_price_eur: e.target.value })} data-testid={`invoice-line-price-${i}`} />
              </div>
              <div className="col-span-3 md:col-span-2">
                <Label className="text-xs">{vatExempt ? 'IVA (esente N4)' : 'IVA %'}</Label>
                <Input type="number" step="1" min="0" max="100" value={vatExempt ? 0 : l.vat_pct} disabled={vatExempt} onChange={(e) => updateLine(i, { vat_pct: e.target.value })} data-testid={`invoice-line-vat-${i}`} />
              </div>
              <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {euro((Number(l.quantity) || 0) * (Number(l.unit_price_eur) || 0) * (1 + (vatExempt ? 0 : (Number(l.vat_pct) || 0)) / 100))}
                </span>
                {lines.length > 1 && (
                  <Button size="icon" variant="ghost" onClick={() => rmLine(i)} aria-label="Rimuovi riga" data-testid={`invoice-line-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Metodo di pagamento</Label>
          <Input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="invoice-payment-method" />
        </div>
        <div>
          <Label>Termini di pagamento</Label>
          <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} data-testid="invoice-payment-terms" />
        </div>
        <div className="md:col-span-2">
          <Label>Note</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="invoice-notes" />
        </div>
      </div>

      {/* Regime fiscale */}
      <div className="rounded-xl border border-border/70 bg-muted/30 p-3 space-y-3" data-testid="invoice-fiscal-box">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Esenzione IVA — Art. 10 n. 18 DPR 633/72</div>
            <p className="text-xs text-muted-foreground">Prestazioni sanitarie: righe a 0% con codice Natura <span className="font-mono">N4</span> nell'XML SDI.</p>
          </div>
          <Switch checked={vatExempt} onCheckedChange={setVatExempt} data-testid="invoice-vat-exempt-toggle" aria-label="Esenzione IVA" />
        </div>
        <div className={cn('flex items-center justify-between gap-3 rounded-lg border p-2.5 transition-colors', totals.bolloDue ? 'border-warning/50 bg-warning/10' : 'border-border/60')} data-testid="invoice-bollo-box">
          <div className="text-xs">
            <div className="font-semibold flex items-center gap-1.5"><Stamp className="h-3.5 w-3.5" /> Marca da bollo virtuale 2,00 €</div>
            <div className="text-muted-foreground" data-testid="invoice-bollo-status">
              {totals.bolloDue
                ? `Applicata automaticamente: importi esenti ${euro(totals.exemptBase)} > 77,47 €`
                : `Non dovuta: importi esenti ${euro(totals.exemptBase)} ≤ 77,47 €`}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs whitespace-nowrap">
            <span className="text-muted-foreground">Addebita al cliente</span>
            <Switch checked={bolloToCustomer} onCheckedChange={setBolloToCustomer} disabled={!totals.bolloDue} data-testid="invoice-bollo-customer-toggle" aria-label="Addebita bollo al cliente" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-xl bg-primary/5 border border-primary/20">
        <div className="space-y-0.5 text-sm">
          <div className="text-muted-foreground">Imponibile: <span className="font-medium">{euro(totals.subtotal)}</span></div>
          <div className="text-muted-foreground">IVA: <span className="font-medium">{euro(totals.vat)}</span></div>
          {totals.bolloDue && <div className="text-muted-foreground">Bollo{!bolloToCustomer && ' (a carico emittente)'}: <span className="font-medium" data-testid="invoice-bollo-amount">{euro(2)}</span></div>}
        </div>
        <div className="text-2xl font-bold text-primary" data-testid="invoice-total">{euro(totals.total)}</div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="invoice-save-btn">
          {saving ? 'Salvataggio...' : 'Emetti fattura'}
        </Button>
      </DialogFooter>
    </div>
  );
};
